import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuth, AuthState, User } from '../hooks/useAuth';

interface AuthContextType {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  loading: boolean;
  login: (credentials: { email: string; password: string }) => Promise<void>;
  logout: () => Promise<void>;
  updateUser: (userData: Partial<User>) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuthContext = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuthContext must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const auth = useAuth();
  const [authState, setAuthState] = useState<AuthState>({
    user: null,
    token: null,
    isAuthenticated: false,
  });
  const [loading, setLoading] = useState(true);

  // Use an effect that only runs once on mount
  useEffect(() => {
    const initializeAuth = async () => {
      try {
        // First, check if we have auth data in localStorage
        setLoading(true);
        const initialState = auth.checkAuth();
        setAuthState(initialState);
        
        // If we're authenticated, try to refresh the user data once
        if (initialState.isAuthenticated && initialState.token) {
          try {
            console.log('Fetching fresh user profile with manager data...');
            const freshUserData = await auth.fetchUserProfile();
            
            if (freshUserData) {
              console.log('Profile data retrieved successfully:', freshUserData);
              
              // Update localStorage directly
              localStorage.setItem('user', JSON.stringify(freshUserData));
              
              // Update state with user data that includes manager info
              setAuthState(prev => ({
                ...prev,
                user: freshUserData
              }));
            }
          } catch (profileError) {
            console.error('Error fetching user profile:', profileError);
            // Continue with the data we have from localStorage
          }
        }
      } catch (error) {
        console.error('Error initializing auth:', error);
      } finally {
        setLoading(false);
      }
    };

    // Only run this once on component mount
    initializeAuth();
    
    // Empty dependency array means this only runs once on mount
  }, []);

  const handleLogin = async (credentials: { email: string; password: string }) => {
    try {
      setLoading(true);
      const authResult = await auth.login(credentials);
      setAuthState(authResult);
    } catch (error) {
      console.error('Login error:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      setLoading(true);
      await auth.logout();
      setAuthState({ user: null, token: null, isAuthenticated: false });
    } catch (error) {
      console.error('Logout error:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateUser = (userData: Partial<User>) => {
    setAuthState(prev => {
      const updated = { ...prev.user, ...userData } as User;
      localStorage.setItem('user', JSON.stringify(updated));
      return { ...prev, user: updated };
    });
  };

  return (
    <AuthContext.Provider
      value={{
        user: authState.user,
        token: authState.token,
        isAuthenticated: authState.isAuthenticated,
        loading,
        login: handleLogin,
        logout: handleLogout,
        updateUser: handleUpdateUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
